"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { trackRegistrationCompleted } from "@/lib/analytics";
import { getFriendlyAuthError } from "@/utils/authErrors";
import {
  validateDisplayName,
  sanitizeDisplayName,
  isDisposableEmail,
} from "@/utils/validation";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import {
  PageWrapper,
  Container,
  Card,
  Title,
  Subtitle,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  GoogleButton,
  Divider,
  ErrorMessage,
  LinkText,
} from "./page.styles";

export default function RegisterPage() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate display name
    const nameValidation = validateDisplayName(name);
    if (!nameValidation.isValid) {
      setError(nameValidation.errors[0]);
      return;
    }

    // Check for disposable email
    if (isDisposableEmail(email)) {
      setError(
        "Temporary email addresses are not allowed. Please use a permanent email.",
      );
      return;
    }

    // Validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      // Create user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Sanitize and update profile with name
      const sanitizedName = sanitizeDisplayName(name);
      await updateProfile(userCredential.user, {
        displayName: sanitizedName,
      });

      // Create user document in Firestore (using 'accounts' collection per Firebase rules)
      await setDoc(doc(db, "accounts", userCredential.user.uid), {
        email,
        displayName: sanitizedName,
        photoURL: null,
        isArtisan: false,
        createdAt: new Date(),
        location: {
          address: "",
          coordinates: null,
        },
      });

      void trackRegistrationCompleted("email");

      // Redirect to account type selection for onboarding
      router.push("/onboarding/account-type");
    } catch (err: any) {
      console.error("Registration error:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setError("");
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Create user document in Firestore if new user (using 'accounts' collection)
      const userDoc = doc(db, "accounts", result.user.uid);
      await setDoc(
        userDoc,
        {
          email: result.user.email,
          displayName: result.user.displayName || "User",
          photoURL: result.user.photoURL,
          isArtisan: false,
          createdAt: new Date(),
          location: {
            address: "",
            coordinates: null,
          },
        },
        { merge: true },
      );

      void trackRegistrationCompleted("google");

      // Redirect to account type selection for onboarding
      router.push("/onboarding/account-type");
    } catch (err: any) {
      console.error("Google registration error:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <PublicHeader />
      <Container>
        <Card>
          <Title>Join Drizl</Title>
          <Subtitle>Create your account to get started</Subtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Form onSubmit={handleEmailRegister}>
            <InputGroup>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
              />
            </InputGroup>

            <Button type="submit" disabled={loading}>
              {loading ? "Creating Account..." : "Sign Up"}
            </Button>
          </Form>

          <Divider>
            <span>OR</span>
          </Divider>

          <GoogleButton
            type="button"
            onClick={handleGoogleRegister}
            disabled={loading}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path
                fill="#4285F4"
                d="M19.6 10.23c0-.82-.1-1.42-.25-2.05H10v3.72h5.5c-.15.96-.74 2.31-2.04 3.22v2.45h3.16c1.89-1.73 2.98-4.3 2.98-7.34z"
              />
              <path
                fill="#34A853"
                d="M13.46 15.13c-.83.59-1.96 1-3.46 1-2.64 0-4.88-1.74-5.68-4.15H1.07v2.52C2.72 17.75 6.09 20 10 20c2.7 0 4.96-.89 6.62-2.42l-3.16-2.45z"
              />
              <path
                fill="#FBBC05"
                d="M3.99 10c0-.69.12-1.35.32-1.97V5.51H1.07A9.973 9.973 0 000 10c0 1.61.39 3.14 1.07 4.49l3.24-2.52c-.2-.62-.32-1.28-.32-1.97z"
              />
              <path
                fill="#EA4335"
                d="M10 3.88c1.88 0 3.13.81 3.85 1.48l2.84-2.76C14.96.99 12.7 0 10 0 6.09 0 2.72 2.25 1.07 5.51l3.24 2.52C5.12 5.62 7.36 3.88 10 3.88z"
              />
            </svg>
            Continue with Google
          </GoogleButton>

          <LinkText>
            Already have an account? <Link href="/login">Login</Link>
          </LinkText>
        </Card>
      </Container>
      <Footer />
    </PageWrapper>
  );
}
